import { Status } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { useMutation } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaMutationOptions } from '@frontend/react-query-helpers';
import { useUnifiedThreadUpdater } from '../../v4/query-updaters';
import { SchemaSMSDataV3Service } from '../service';
import { SMSMutationContext } from '../types';
import { OptimisticUpdateStatusDetails, ResendSMSIO } from '../types';

export const getResendSMSMutationKey = () => ['V3', 'ResendSMS'] as const;

type MutationContext<C = unknown> = {
  internalContext?: SMSMutationContext;
  otherContext?: C;
};

type ExtendedResendSMSRequest = ResendSMSIO['input'] & {
  threadId?: string;
  locationId: string;
};

type UseResendSMSMutationArgs<
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
> = {
  options?: LimitedSchemaMutationOptions<
    { input: ExtendedResendSMSRequest; output: ResendSMSIO['output'] },
    E,
    C,
    never,
    RequestOverride
  >;
  httpOptions?: Options;
  optimisticUpdate?: boolean;
};
/**
 * A hook that returns a mutation for the `ResendSMS` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useResendSMSMutation = <
  E = unknown,
  C = unknown,
  RequestOverride extends ExtendedResendSMSRequest = ExtendedResendSMSRequest
>({
  options,
  httpOptions,
  optimisticUpdate = false,
}: UseResendSMSMutationArgs<E, C | undefined, RequestOverride> = {}) => {
  const unifiedThreadUpdater = useUnifiedThreadUpdater({ httpOptions });

  return useMutation<ResendSMSIO['output'], E, RequestOverride, MutationContext<C>>({
    mutationKey: getResendSMSMutationKey(),
    mutationFn: ({ threadId, ...req }) => SchemaSMSDataV3Service.ResendSMS(req, httpOptions),
    ...options,
    onMutate: async (request) => {
      const internalContext: SMSMutationContext = {
        revertOptimisticUpdate: () => {},
      };
      if (optimisticUpdate) {
        if (request.threadId) {
          const upsertThreadResult = await unifiedThreadUpdater.upsertThreadItem({
            matchValues: {
              threadItemId: request.smsId,
              threadId: request.threadId,
              locationId: request.locationId,
              entityType: EntityType.ENTITY_TYPE_SMS, // resend applies only for sms thread item.
            },
            updater: (threadItem) => {
              if (threadItem && 'smsMessage' in threadItem) {
                return {
                  smsMessage: {
                    ...threadItem.smsMessage,
                    status: Status.STATUS_NOT_SENT,
                    statusDetails: OptimisticUpdateStatusDetails.SENDING,
                  },
                };
              }
              return threadItem;
            },
            isOptimistic: true,
          });
          internalContext.revertOptimisticUpdate = () => {
            upsertThreadResult.revertCacheUpdate();
          };
        }
      }

      return {
        internalContext,
        otherContext: await options?.onMutate?.(request),
      };
    },
    onSuccess: async (response, request, context) => {
      if (response.sms.threadId) {
        await unifiedThreadUpdater.upsertThreadItem({
          matchValues: {
            threadItemId: request.smsId,
            threadId: response.sms.threadId,
            locationId: request.locationId,
            entityType: EntityType.ENTITY_TYPE_SMS, // resend applies only for sms thread item.
          },
          updater: () => ({
            smsMessage: response.sms,
          }),
          isOptimistic: false,
        });
        unifiedThreadUpdater.invalidateListUnifiedThreads();
      }
      return options?.onSuccess?.(response, request, context?.otherContext);
    },
    onError: (error, request, context) => {
      if (optimisticUpdate && context?.internalContext) {
        context.internalContext.revertOptimisticUpdate?.();
      }
      return options?.onError?.(error, request, context?.otherContext);
    },
    onSettled: (response, error, request, context) => {
      // Only pass context of type C into provided `onSettled` option
      return options?.onSettled?.(response, error, request, context?.otherContext);
    },
  });
};
